import { ComponentProps, Ref, Suspense, forwardRef } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Badge } from "@/kit/ui/badge";
import { useDebounced } from "@/utils/hooks";
import {
  ActivityKind,
  TagInputQuery,
} from "./__generated__/TagInputQuery.graphql";
import AutoComplete_, {
  AutoCompleteT,
  useAutoCompleteInputValue,
} from "./AutoComplete";
import { XIcon } from "@/icons";
import { Button } from "@/kit/ui/button";
import { FormattedMessage } from "react-intl";

const AutoComplete: AutoCompleteT<string> = AutoComplete_;

export interface TagInputProps extends Omit<
  ComponentProps<"input">,
  "value" | "onChange"
> {
  value: string[] | undefined;
  onChange: (value: string[]) => void;
  activityKind?: ActivityKind | undefined;
}

export type { ActivityKind };

const Query = graphql`
  query TagInputQuery($search: String, $activityKind: ActivityKind) {
    tags(first: 6, filters: { search: $search, activityKind: $activityKind }) {
      nodes {
        name
      }
    }
  }
`;

export default forwardRef(function TagInput(
  { value, onChange, activityKind, ...inputProps }: TagInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const onAddTag = (tagName: string) => {
    if (value?.includes(tagName)) return;
    const next = value ? value.slice() : [];
    next.push(tagName);
    onChange?.(next);
  };

  const onRemoveTagAt = (index: number) => {
    if (!value?.length) return;
    const next = value.slice();
    if (index < 0) {
      index = value.length + index;
    }
    next.splice(index, 1);
    onChange?.(next);
  };

  return (
    <AutoComplete.Root
      onSelectItem={onAddTag}
      onPopItem={() => onRemoveTagAt(-1)}
    >
      <AutoComplete.Input
        ref={ref}
        {...inputProps}
        className="flex flex-wrap gap-2"
        onSubmitItem={onAddTag}
      >
        {value?.map((tagName, index) => (
          <Badge key={tagName}>
            <span className="first-letter:uppercase">{tagName}</span>
            <button type="button" onClick={onRemoveTagAt.bind(null, index)}>
              <XIcon className="w-4 h-4" />
            </button>
          </Badge>
        ))}
      </AutoComplete.Input>

      <AutoComplete.Portal className="aria-busy:pointer-events-none aria-busy:text-grey-600">
        <Suspense
          fallback={
            <span className="text-sm">
              <FormattedMessage defaultMessage="Please wait..." />
            </span>
          }
        >
          <TagList activityKind={activityKind} onAddTag={onAddTag} />
        </Suspense>
      </AutoComplete.Portal>
    </AutoComplete.Root>
  );
});

interface TagListProps {
  activityKind?: ActivityKind | undefined;
  onAddTag?(tagName: string): void;
}

function TagList({ activityKind, onAddTag }: TagListProps) {
  const search = useAutoCompleteInputValue();
  const search_ = useDebounced(search, 800);
  const { tags } = useLazyLoadQuery<TagInputQuery>(Query, {
    search: search_,
    activityKind,
  });

  if (!tags.nodes.length) {
    return (
      <span className="text-sm">
        <FormattedMessage
          defaultMessage={`Create: "{tag}"`}
          values={{ tag: search }}
        />
      </span>
    );
  }

  return (
    <AutoComplete.List>
      {tags.nodes.map((tag) => (
        <AutoComplete.ListItem
          key={tag.name}
          value={tag.name}
          onDoubleClick={onAddTag?.bind(null, tag.name)}
        >
          <span className="flex-1">{tag.name}</span>
          <Button
            variant="tertiary"
            size="xs"
            onClick={onAddTag?.bind(null, tag.name)}
            type="button"
          >
            <FormattedMessage defaultMessage="Add" />
          </Button>
        </AutoComplete.ListItem>
      ))}
    </AutoComplete.List>
  );
}
